import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { GeneralSummary } from "@/types/analysisSummary";

export interface AnalysisSummaryState {
  analysisSummaryId: string | null;
  generalSummary: GeneralSummary | null;
  spatialAnalysisDatasetId: string | null;
  selectedAttribute: IOptionItem;

  isHasSummary: boolean;
  gridsSelected: string[];
}

export const analysisSummaryInitialState: AnalysisSummaryState = {
  analysisSummaryId: null,
  generalSummary: null,
  spatialAnalysisDatasetId: null,
  isHasSummary: false,
  selectedAttribute: { label: "Score", value: "Score" },
  gridsSelected: [DROPDOWN_ALL_VALUE]
};

export const analysisSummarySlice = createSlice({
  name: "analysisSummary",
  initialState: analysisSummaryInitialState,
  reducers: {
    setAnalysisSummaryId: (state, action: PayloadAction<string | null>) => {
      state.analysisSummaryId = action.payload;
    },
    setGeneralSummary: (
      state,
      action: PayloadAction<GeneralSummary | null>
    ) => {
      state.generalSummary = action.payload;
    },
    setSpatialAnalysisDatasetId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.spatialAnalysisDatasetId = action.payload;
    },
    setIsHasSummary: (state, action: PayloadAction<boolean>) => {
      state.isHasSummary = action.payload;
    },
    addGridSelected: (state, action: PayloadAction<string>) => {
      let grids = state.gridsSelected?.filter(
        (item) => item !== DROPDOWN_ALL_VALUE
      );
      // Remove the grid if it already exists in the selection
      if (grids.includes(action.payload)) {
        grids = grids.filter((item) => item !== action.payload);
        state.gridsSelected = grids.length > 0 ? grids : [DROPDOWN_ALL_VALUE];
      } else if (grids.length < 3)
        state.gridsSelected = [...grids, action.payload];
    },
    setGridsSelected: (state, action: PayloadAction<string[]>) => {
      const grids = action.payload;
      if (grids.length === 1 && grids.includes(DROPDOWN_ALL_VALUE)) {
        state.gridsSelected = grids;
      } else {
        state.gridsSelected = action.payload?.filter(
          (item) => item !== DROPDOWN_ALL_VALUE
        );
      }
    },
    setSelectedAttribute: (state, action: PayloadAction<IOptionItem>) => {
      state.selectedAttribute = action.payload;
    },
    resetGridsSelected: (state) => {
      state.gridsSelected = [DROPDOWN_ALL_VALUE];
    },
    resetAnalysisSummaryState: () => analysisSummaryInitialState
  }
});

export const {
  setAnalysisSummaryId,
  setGeneralSummary,
  setIsHasSummary,
  setSelectedAttribute,
  addGridSelected,
  setGridsSelected,
  resetGridsSelected,
  setSpatialAnalysisDatasetId,
  resetAnalysisSummaryState
} = analysisSummarySlice.actions;

export const analysisSummaryReducer = analysisSummarySlice.reducer;
